import { isAuthenticated } from "@biggeo/bg-common";
import { getVelocityUrlOnLocal } from "@biggeo/bg-utils";
import {
    Failure,
    Initialized,
    Pending,
    RemoteData,
    Success,
} from "@vividtheory/remotedata";
import * as A from "fp-ts/Array";
import { pipe } from "fp-ts/lib/function";
import compact from "lodash/compact";
import groupBy from "lodash/groupBy";
import uniqBy from "lodash/uniqBy";
import LogRocket from "logrocket";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { commonActions } from "../../common/redux/model";
import { unwrapMaybe } from "../../common/utils/helpers";
import {
    useErrorToast,
    useSuccessToast,
} from "../../toaster/containers/redux/hooks";
import { useAuthenticatedUser } from "../../user/redux/hooks";
import { AuroraClient, createAuroraClient } from "../../utils/socket";
import { AuroraError } from "../../velocity/aurora/common";
import {
    DataReq,
    PointDataReq,
    PointDataRspSuccess,
    SchemaReq,
    SchemaRspSuccess,
} from "../../velocity/aurora/map";
import { SOCKET_CONNECTION_MESSAGES } from "../filter-criteria/utils/utils";
import { useMap } from "../mapbox/context";
import { useMapStateUtilities } from "../mapbox/context/hooks";
import {
    convertSocketStringResponseToSubscriptionResponse,
    createSocket,
} from "../utils/subscription";

const getAuroraClient = ({
    serverUrl,
    userJwt,
    dataSourceIds,
}: {
    readonly serverUrl: string;
    readonly userJwt: string | undefined;
    readonly dataSourceIds: string[];
}) => {
    return createAuroraClient(
        createSocket({
            serverUrl,
            crate: "aurora",
            path: "/velocity.io",
            userJwt,
        }),
        serverUrl,
        dataSourceIds
    );
};

export const useAuroraSocketClient = ({
    serverUrl,
}: {
    readonly serverUrl: string;
}) => {
    const successToast = useSuccessToast();
    const user = useAuthenticatedUser();
    const userJwt = isAuthenticated(user) ? user.jwt : undefined;

    const [client, setClient] = useState<AuroraClient>();

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        const newClient = getAuroraClient({
            serverUrl,
            userJwt,
            dataSourceIds: [],
        });
        successToast(SOCKET_CONNECTION_MESSAGES.connected);

        setClient(newClient);

        return () => {
            client?.disconnect();
        };
    }, [serverUrl, userJwt]);

    return client;
};

export const useAuroraSchemaQuery = (
    input: SchemaReq,
    client?: AuroraClient,
    serverUrl?: string
) => {
    const errorToast = useErrorToast();

    const [schemaRemoteData, setSchemaRemoteData] = useState<
        RemoteData<AuroraError, SchemaRspSuccess>
    >(new Initialized());

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (serverUrl && serverUrl === client?.serverUrl) {
            client?.callBacks.sendSchemaQuery(input);

            client?.callBacks.onSchemaQueryResult((response) => {
                if (response.err) {
                    setSchemaRemoteData(new Failure(response.err));
                    errorToast(response.err.message);
                    return;
                }

                if (response.success) {
                    setSchemaRemoteData(
                        new Success({
                            rows: response.success.rows.map((row) => ({
                                columnName: row.columnName,
                                dataType: row.dataType,
                                attributes: row.attributes,
                            })),
                        })
                    );
                }
            });
        }
    }, [input.datasetId, input.organizationId, client]);

    return {
        schemaRemoteData,
    };
};

export const useAuroraDataQuery = (
    input: DataReq,
    databaseId: string,
    client?: AuroraClient,
    serverUrl?: string
) => {
    const errorToast = useErrorToast();
    const { dispatch, socketResponses } = useMap();
    const data = socketResponses.filteredDataResponse
        ? socketResponses.filteredDataResponse[databaseId]
        : new Initialized();
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (serverUrl && serverUrl === client?.serverUrl) {
            dispatch?.({
                type: "SET_SOCKET_FILTERED_DATA_RESPONSE",
                values: {
                    databaseId,
                    data: new Pending(),
                },
            });
            client?.callBacks.sendDataQuery(input);

            client?.callBacks.onDataQueryResult((response) => {
                if (response.err) {
                    LogRocket.log(response.err);
                    dispatch?.({
                        type: "SET_SOCKET_FILTERED_DATA_RESPONSE",
                        values: {
                            databaseId,
                            data: new Failure(response.err),
                        },
                    });
                    errorToast(response.err.message);
                    return;
                }

                if (response.success) {
                    dispatch?.({
                        type: "SET_SOCKET_FILTERED_DATA_RESPONSE",
                        values: {
                            databaseId,
                            data: new Success({
                                count: response.success.count,
                                rows: response.success.rows,
                            }),
                        },
                    });
                }
            });
        }
    }, [JSON.stringify(input), client, databaseId]);

    return {
        dataRemoteData: data || new Initialized(),
    };
};

export const useAuroraPointDataQuery = (
    input: PointDataReq,
    serverUrl?: string,
    client?: AuroraClient
): {
    dataRemoteData: RemoteData<AuroraError, PointDataRspSuccess>;
} => {
    const errorToast = useErrorToast();
    const [dataRemoteData, setDataRemoteData] = useState<
        RemoteData<AuroraError, PointDataRspSuccess>
    >(new Initialized());

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (serverUrl && serverUrl === client?.serverUrl) {
            setDataRemoteData(new Pending());

            client?.callBacks.sendPointDataQuery(input);

            client?.callBacks.onPointDataQueryResult((response) => {
                if (response.err) {
                    LogRocket.log(response.err);
                    setDataRemoteData(new Failure(response.err));
                    errorToast(response.err.message);
                    return;
                }

                if (response.success) {
                    setDataRemoteData(
                        new Success({
                            row: response.success.row,
                        })
                    );
                }
            });
        }
    }, [JSON.stringify(input), client]);

    return {
        dataRemoteData,
    };
};

/**
 * Custom hook that manages WebSocket connections for multiple data sources.
 * Groups data sources by server URL and creates a socket connection for each unique server.
 *
 * @param dataSources - Array of data source objects containing id and serverUrl
 * @param callbacks - Object containing callback functions for socket events
 *
 * @returns Array of BGSocket instances, each representing a configured WebSocket connection
 *
 * @remarks
 * - Creates one socket connection per unique serverUrl to optimize network resources
 * - Automatically handles connection events (connect, disconnect, error) with toast notifications
 * - Re-establishes connections when dataSources change
 *
 * @example
 * ```typescript
 * const callbacks = {
 *   onMessage: () => {
 *      //Do something
 *   },
 * };
 *
 * const sockets = useDataSourceSockets(dataSources, callbacks);
 * ```
 */
export const useAuroraClients = (
    dataSources: readonly { id: string; serverUrl: string }[],
    userJwt?: string
): readonly AuroraClient[] => {
    const errorToast = useErrorToast();
    const successToast = useSuccessToast();

    const [sockets, setSockets] = useState<readonly AuroraClient[]>([]);
    const onConnect = () => successToast(SOCKET_CONNECTION_MESSAGES.connected);
    const onDisconnect = () =>
        errorToast(SOCKET_CONNECTION_MESSAGES.disconnectedRetry);
    const onError = () => errorToast(SOCKET_CONNECTION_MESSAGES.disconnected);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        const groupedDataSources = groupBy(dataSources, "serverUrl");

        // Update existing sockets or create new ones
        const updatedSockets = Object.entries(groupedDataSources).map(
            ([serverUrl, items]) => {
                // Find existing socket for this server URL
                const existingSocket = sockets.find(
                    (socket) => socket.serverUrl === serverUrl
                );

                if (existingSocket) {
                    return createAuroraClient(
                        existingSocket,
                        serverUrl,
                        items.map((c) => c.id)
                    );
                }

                // Create new socket if none exists for this server URL
                const socket = createSocket({
                    serverUrl,
                    crate: "aurora",
                    path: "/velocity.io",
                    userJwt,
                });
                onConnect();

                return createAuroraClient(
                    socket,
                    serverUrl,
                    items.map((c) => c.id)
                );
            }
        );

        setSockets(updatedSockets);
        // Clean Up
        return () => {
            updatedSockets.map((c) => c.disconnect());
        };
    }, [JSON.stringify(dataSources)]);

    return sockets;
};

/**
 * A hook that manages WebSocket connections for multiple data sources.
 *
 * @param dataSources - An array of data source objects containing id and serverUrl
 * @param callbacks - Callback functions to handle WebSocket events
 *
 * @returns An object containing:
 * - sockets: Array of active WebSocket connections
 * - getSocketsForDataSources: Function to filter sockets by data source IDs
 *
 */
export const useWebSocketConnections = (
    dataSources: readonly { id: string; serverUrl: string }[]
) => {
    const user = useAuthenticatedUser();

    const sockets = useAuroraClients(
        dataSources,
        isAuthenticated(user) ? user.jwt : undefined
    );

    /**
     * Retrieves unique WebSocket connections (BGSockets) associated with specified data source IDs.
     *
     * @param dataSourceIds - An array of data source identifiers to filter sockets by
     * @returns An array of unique BGSocket instances that are connected to any of the specified data sources
     *
     * The function performs the following operations:
     * 1. Filters sockets to only those containing any of the specified dataSourceIds
     * 2. Removes duplicate sockets based on their id
     */
    const getSocketsForDataSources = (
        dataSourceIds: readonly string[]
    ): readonly AuroraClient[] => {
        const getSocketsWithDataSourceIds = (
            sockets: readonly AuroraClient[]
        ) =>
            sockets.filter((val) =>
                val.dataSourceIds.some((id) => dataSourceIds.includes(id))
            );

        const getUniqueSockets = (sockets: readonly AuroraClient[]) =>
            uniqBy(sockets, "id");

        return pipe(sockets, getSocketsWithDataSourceIds, getUniqueSockets);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        return () => {
            sockets.map((socket) => socket.disconnect());
        };
    }, []);

    return {
        sockets,
        getSocketsForDataSources,
    };
};

export const useAuroraSpatialData = (sockets: readonly AuroraClient[]) => {
    const dispatch = useDispatch();
    const { datasets } = useMap();

    const { setLoading, setSocketResponses } = useMapStateUtilities();

    const dataSourcesToConnect = pipe(
        datasets,
        A.map((ds) => {
            const isDevelopment = import.meta.env.MODE === "development";
            const serverUrl = unwrapMaybe(ds.dataSourceConfiguration.serverUrl);

            const url =
                isDevelopment && serverUrl
                    ? getVelocityUrlOnLocal(serverUrl)
                    : unwrapMaybe(ds.dataSourceConfiguration.serverUrl);

            if (url) {
                return {
                    id: ds.dataSource.id,
                    serverUrl: url,
                };
            }

            return undefined;
        }),
        compact
    );

    sockets.map((c) => {
        c.callBacks.onSpatialDataQueryResult((data) => {
            const dataset = dataSourcesToConnect.find(
                (d) => d.id === data.databaseId
            );

            if (!dataset) return;

            setLoading({ data: true });

            const datasetId = dataset?.id;

            if (datasetId) {
                dispatch(
                    commonActions.setSearchEndTime({
                        datasetId,
                        endTime: Date.now(),
                    })
                );
                dispatch(
                    commonActions.setResponseParseStartTime({
                        databaseId: datasetId,
                        startTime: Date.now(),
                    })
                );
            }
            const converted =
                convertSocketStringResponseToSubscriptionResponse(data);
            if (datasetId) {
                dispatch(
                    commonActions.setResponseParseEndTime({
                        databaseId: datasetId,
                        endTime: Date.now(),
                    })
                );
            }
            // Add response to set of socket responses
            setSocketResponses(converted);
            if (datasetId) {
                dispatch(
                    commonActions.setSearchEndTime({
                        datasetId: datasetId,
                        endTime: Date.now(),
                    })
                );
            }

            setLoading({ data: false });
        });
    });
};
